// =============================================================
// Náborovka — frontend
//
// Bez frameworku. Tři drobnosti, které stránka potřebuje:
// podložení hlavičky po odrolování, mobilní menu a jedno
// odhalení obsahu při scrollu. Posuvník referencí (Swiper)
// je samostatný bundl, dotahuje se jen tam, kde je potřeba.
// =============================================================

import { initCookieConsent } from './cookieConsent.js';
import { initPopups } from './popups.js';

document.documentElement.classList.add('js');

initCookieConsent();

// ── Hlavička ─────────────────────────────────────────────────

function initStickyHeader() {
    const header = document.querySelector('.ln-header');
    if (!header) return;

    const onScroll = () => header.classList.toggle('is-stuck', window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
}

// ── Mobilní menu ─────────────────────────────────────────────

function initMobileNav() {
    const nav    = document.getElementById('lnMobileNav');
    const opener = document.querySelector('[data-nav-open]');
    if (!nav || !opener) return;

    const focusables = () => nav.querySelectorAll('a[href], button:not([disabled])');

    function open() {
        nav.classList.add('is-open');
        nav.removeAttribute('inert');
        document.body.classList.add('ln-nav-open');
        opener.setAttribute('aria-expanded', 'true');
        focusables()[0]?.focus();
    }

    function close() {
        nav.classList.remove('is-open');
        // `inert` vyndá zavřené menu z tabulátoru — jinak se přes něj
        // dá proklikat na odkazy, které nikdo nevidí.
        nav.setAttribute('inert', '');
        document.body.classList.remove('ln-nav-open');
        opener.setAttribute('aria-expanded', 'false');
        opener.focus();
    }

    nav.setAttribute('inert', '');
    opener.addEventListener('click', open);
    nav.querySelector('[data-nav-close]')?.addEventListener('click', close);
    nav.querySelectorAll('a').forEach(a => a.addEventListener('click', close));

    document.addEventListener('keydown', (e) => {
        if (!nav.classList.contains('is-open')) return;
        if (e.key === 'Escape') { close(); return; }

        // Tabulátor zůstává v otevřeném menu (role="dialog", aria-modal) —
        // jinak by fokus odešel na stránku, kterou menu zakrývá.
        if (e.key === 'Tab') {
            const list  = [...focusables()];
            const first = list[0];
            const last  = list[list.length - 1];
            if (!first) return;
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
    });
}

// ── Lead formuláře (objednávka, poptávka v dialogu) ──────────
//
// Odesílá se přes FormData, ne jako JSON — jinak by neprošly přílohy.
// Odpověď se čte benevolentně: v dev režimu umí PHP warning rozbít JSON
// a formulář by pak mlčel, i když lead ve skutečnosti dorazil.

function initLeadForms() {
    document.querySelectorAll('form[data-lead-form]').forEach(initLeadForm);
}

function initLeadForm(form) {
    const btn = form.querySelector('button[type=submit]');
    const msg = form.querySelector('.ln-form__msg');

    // Skript reCAPTCHY (~450 kB i s fonty) se stahuje až ve chvíli, kdy
    // s formulářem někdo začne pracovat.
    if (form.dataset.recaptchaKey) {
        const warm = () => loadRecaptcha(form.dataset.recaptchaKey).catch(() => {});
        ['pointerover', 'focusin', 'touchstart'].forEach(ev => form.addEventListener(ev, warm, { once: true, passive: true }));
    }

    form.addEventListener('submit', async (e) => {
        e.preventDefault();

        if (!form.reportValidity()) return;

        btn.disabled = true;
        msg.textContent = '';
        msg.className = msg.className.replace(/\bis-(ok|err)\b/g, '').trim();

        // Token reCAPTCHY platí jen dvě minuty, takže se bere až teď.
        await fillRecaptchaToken(form);

        try {
            const res = await fetch(form.action, {
                method: 'POST',
                body: new FormData(form),
                headers: { 'X-Requested-With': 'XMLHttpRequest' },
            });

            let data = {};
            try { data = await res.json(); } catch { /* viz poznámka výš */ }

            if (res.ok && data.ok) {
                // Vybraná pozice a cena se resetem nesmí ztratit — vrátí se z výběru.
                const keep = [...form.querySelectorAll('[data-order-position], [data-order-price-input]')].map(el => [el, el.value]);
                form.reset();
                keep.forEach(([el, v]) => { el.value = v; });
                msg.textContent = form.dataset.success || 'Děkujeme, ozveme se vám co nejdřív.';
                msg.classList.add('is-ok');
            } else {
                msg.textContent = data.error || 'Odeslání se nepovedlo. Zkuste to prosím znovu.';
                msg.classList.add('is-err');
            }
        } catch {
            msg.textContent = 'Odeslání se nepovedlo. Zkuste to prosím znovu.';
            msg.classList.add('is-err');
        }

        btn.disabled = false;
    });
}

// ── Balíčky → objednávka ─────────────────────────────────────
//
// Karta balíčku a výběr „1. Pozice" jsou dvě podoby téže volby. Klik na
// kartu ji vybere (tmavá karta), přepne pozici a cenu v objednávce
// a posune na formulář; změna výběru ve formuláři přepne kartu zpět.

function initPackages() {
    const cards  = [...document.querySelectorAll('[data-package]')];
    const select = document.querySelector('[data-order-position]');
    if (!cards.length || !select) return;

    const priceOut = document.querySelector('[data-order-price]');
    const priceIn  = document.querySelector('[data-order-price-input]');

    const apply = (index) => {
        cards.forEach((c, i) => {
            c.classList.toggle('is-selected', i === index);
            c.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
        select.selectedIndex = index;
        const price = select.options[index]?.dataset.price || '';
        if (priceOut) priceOut.textContent = price;
        if (priceIn)  priceIn.value = price;
    };

    cards.forEach((card, i) => card.addEventListener('click', () => {
        apply(i);
        document.getElementById('objednavka')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }));
    select.addEventListener('change', () => apply(select.selectedIndex));
}

// ── Dialogy ──────────────────────────────────────────────────
//
// Nativní <dialog>: fokus, Escape a inertní pozadí řeší prohlížeč sám.
// Kotva #poradit (odkaz z podstránek) dialog otevře po načtení.

function initDialogs() {
    const open = (id) => {
        const dlg = document.getElementById(id);
        if (dlg && !dlg.open) dlg.showModal();
    };

    document.querySelectorAll('[data-dialog-open]').forEach(btn =>
        btn.addEventListener('click', () => open(btn.dataset.dialogOpen)));

    document.querySelectorAll('dialog').forEach((dlg) => {
        dlg.querySelectorAll('[data-dialog-close]').forEach(b => b.addEventListener('click', () => dlg.close()));
        // Klik na podklad (mimo obsah) dialog zavře.
        dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
    });

    if (location.hash === '#poradit') open('nbPoptavka');
}

/**
 * Doplní token reCAPTCHY do skrytého pole formuláře.
 *
 * Když ochrana není zapnutá, formulář data-atributy nemá a funkce se
 * hned vrací. Selhání se jen zaloguje a odeslání pokračuje — server si
 * token stejně ověřuje sám a je lepší poslat poptávku bez tokenu než
 * ji kvůli výpadku cizího skriptu ztratit.
 */
async function fillRecaptchaToken(form) {
    const key = form.dataset.recaptchaKey;
    const field = form.querySelector('input[name="g-recaptcha-response"]');
    if (!key || !field) return;

    try {
        await loadRecaptcha(key);
        await new Promise(resolve => window.grecaptcha.ready(resolve));
        field.value = await window.grecaptcha.execute(key, { action: form.dataset.recaptchaAction || 'submit' });
    } catch (err) {
        console.warn('[reCAPTCHA] token se nepodařilo získat', err);
    }
}

let recaptchaLoading = null;

/** Načte api.js reCAPTCHY jednou za stránku; po 10 s to vzdá (odeslání pak jde bez tokenu). */
function loadRecaptcha(key) {
    if (window.grecaptcha) return Promise.resolve();
    if (recaptchaLoading) return recaptchaLoading;

    recaptchaLoading = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(key);
        s.async = true;
        const timer = setTimeout(() => reject(new Error('timeout')), 10000);
        s.onload  = () => { clearTimeout(timer); resolve(); };
        s.onerror = () => { clearTimeout(timer); reject(new Error('load failed')); };
        document.head.appendChild(s);
    });
    // Po selhání zkusit znovu při dalším pokusu, ne navždy vrátit odmítnutý slib.
    recaptchaLoading.catch(() => { recaptchaLoading = null; });
    return recaptchaLoading;
}

// ── Odhalení obsahu ──────────────────────────────────────────
//
// IntersectionObserver místo knihovny na animace — jedno gesto
// nestojí za další balík v bundlu. Bez podpory (nebo s vypnutým
// pohybem) se obsah jen zobrazí.
//
// Odhaluje se vždy celý blok, ne jednotlivé karty. Po kartách to
// nechávalo mřížku dlouho poloprázdnou a na pomalém scrollu z toho
// byla šedá díra místo obsahu.

function initReveal() {
    const items = document.querySelectorAll('.ln-reveal');
    if (!items.length) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || !('IntersectionObserver' in window)) {
        items.forEach(el => el.classList.add('is-visible'));
        return;
    }

    const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
        });
    }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });

    items.forEach(el => io.observe(el));
}

// ── Napočítání důvěrobodů ────────────────────────────────────
//
// Hodnota začínající číslem („12+", „250 000", „98 %") naběhne od nuly,
// když pruh dojede do obrazu. Mění se jen číslice, zbytek textu stojí,
// a šířka se drží (tabular-nums), takže nic neposkakuje. Čtečka čte
// skrytou hotovou hodnotu (trust-items.php); s vypnutým pohybem se nic
// neanimuje.

function initTrustCount() {
    const items = document.querySelectorAll('[data-trust-count]');
    if (!items.length) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !('IntersectionObserver' in window)) return;

    // Rozpad hodnoty na číslo a zbytek; nula se ukáže hned, ať hotová
    // hodnota neproblikne dřív, než se začne počítat.
    const parse = (el) => {
        const text  = el.textContent;
        const match = text.match(/^(\s*)(\d[\d\s\u00a0]*)(.*)$/s);
        const target = match ? parseInt(match[2].replace(/\D/g, ''), 10) : 0;
        if (!target) return null;
        const grouped = /[\s\u00a0]/.test(match[2].trim());
        const format  = (n) => grouped ? n.toLocaleString('cs-CZ') : String(n);
        el.textContent = match[1] + format(0) + match[3];
        return { el, text, match, target, format };
    };

    const run = ({ el, text, match, target, format }) => {
        const duration = 1400;
        const start    = performance.now();
        const step = (now) => {
            const p = Math.min(1, (now - start) / duration);
            const eased = 1 - Math.pow(1 - p, 3);
            el.textContent = match[1] + format(Math.round(target * eased)) + match[3];
            if (p < 1) requestAnimationFrame(step);
            else el.textContent = text;
        };
        requestAnimationFrame(step);
    };

    const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            io.unobserve(entry.target);
            run(state.get(entry.target));
        });
    }, { threshold: .6 });

    const state = new Map();
    items.forEach((el) => {
        const s = parse(el);
        if (!s) return;
        state.set(el, s);
        io.observe(el);
    });
}

// ── Široké tabulky v textu ───────────────────────────────────
//
// Na úzkém displeji se tabulka v textu (.ln-prose) posouvá do strany
// (_page.scss). Posuvná oblast musí jít ovládat i klávesnicí (WCAG
// 2.1.1) — dostane tabindex, a to jen když se opravdu nevejde, ať
// v tabulátoru nepřibývají zastávky navíc. Role tabulky zůstává.

function initScrollableTables() {
    const tables = document.querySelectorAll('.ln-prose table');
    if (!tables.length) return;

    const update = () => tables.forEach((t) => {
        if (t.scrollWidth > t.clientWidth + 1) {
            if (!t.hasAttribute('tabindex')) t.setAttribute('tabindex', '0');
        } else if (t.getAttribute('tabindex') === '0') {
            t.removeAttribute('tabindex');
        }
    });
    update();
    window.addEventListener('resize', update, { passive: true });
}

// ── Posuvník referencí ───────────────────────────────────────
//
// Swiper je samostatný bundl — načte se jen na stránce s blokem
// referencí. Verzi (kvůli cache) přebírá od app.min.js: oba bundly
// vznikají v jednom kroku sestavení.

const APP_VERSION = (() => {
    try { return new URL(document.currentScript.src).searchParams.get('v') || ''; }
    catch { return ''; }
})();

function initRefSliders() {
    if (!document.querySelector('[data-refswiper]')) return;
    const s = document.createElement('script');
    s.src   = '/www/assets/js/dist/refslider.min.js' + (APP_VERSION ? '?v=' + APP_VERSION : '');
    s.async = true;
    document.head.appendChild(s);
}

document.addEventListener('DOMContentLoaded', () => {
    initStickyHeader();
    initMobileNav();
    initLeadForms();
    initPackages();
    initDialogs();
    initReveal();
    initTrustCount();
    initRefSliders();
    initScrollableTables();
    initPopups();
});
